import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, UserPlus, Shield, Crown } from 'lucide-react';
import { ProtectedComponent } from '@/components/common/ProtectedComponent';
import { StaffPermissionList } from '@/components/staff/StaffPermissionList';
import { PermissionManager } from '@/components/staff/PermissionManager';
import { Permission, Role } from '@/types/permissions';
import { StaffUser } from '@/types';
import { api } from '@/services/api';
import toast from 'react-hot-toast';

interface CreateForm {
  username: string;
  email: string;
  full_name: string;
  password: string;
  role: Role;
  send_welcome_email: boolean;
  ai_role: 'super_admin' | 'manager' | 'supervisor' | 'staff';
  ai_enabled: boolean;
}

const ROLES: Role[] = [Role.ADMIN, Role.MANAGER, Role.SUPERVISOR, Role.STAFF, Role.TRAINEE];

const StaffManagement: React.FC = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'list' | 'edit' | 'create'>('list');
  const [selectedUser, setSelectedUser] = useState<StaffUser | null>(null);

  // Create form state
  const [createForm, setCreateForm] = useState<CreateForm>({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: Role.STAFF,
    send_welcome_email: true,
    ai_role: 'staff',
    ai_enabled: true,
  });
  const [createError, setCreateError] = useState<string | null>(null);
  const [createLoading, setCreateLoading] = useState(false);

  const handleViewUser = (user: StaffUser) => {
    setSelectedUser(user);
    setViewMode('edit');
  };

  const handleEditUser = (user: StaffUser) => {
    setSelectedUser(user);
    setViewMode('edit');
  };

  const handleCreateUser = () => {
    setCreateForm({
      username: '',
      email: '',
      full_name: '',
      password: '',
      role: Role.STAFF,
      send_welcome_email: true,
      ai_role: 'staff',
      ai_enabled: true,
    });
    setCreateError(null);
    setViewMode('create');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedUser(null);
  };

  const handleSavePermissions = async (userId: number, permissions: Permission[], role: Role) => {
    const result = await api.staff.update(userId, { permissions, role });
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success(t('common.saved'));
    }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    setCreateLoading(true);
    try {
      const result = await api.staff.create({
        username: createForm.username.trim(),
        email: createForm.email.trim(),
        full_name: createForm.full_name.trim(),
        password: createForm.password,
        role: createForm.role,
        send_welcome_email: createForm.send_welcome_email,
        ai_role: createForm.ai_role,
        ai_enabled: createForm.ai_enabled,
      });
      if (result.error) {
        setCreateError(result.error);
      } else {
        toast.success(t('common.saved'));
        setViewMode('list');
      }
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <ProtectedComponent
      permission={Permission.VIEW_STAFF}
      fallback={
        <div className="text-center py-12">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('errors.unauthorized')}
          </h3>
          <p className="text-gray-600">
            {t('errors.staffManagementUnauthorized')}
          </p>
        </div>
      }
    >
      <div className="space-y-6">
        {viewMode === 'list' && (
          <>
            {/* 页面标题 */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <Users className="h-8 w-8 text-primary-600" />
                  {t('settings.staffManagement')}
                </h1>
                <p className="text-gray-600 mt-1">
                  {t('settings.staffManagementDesc')}
                </p>
              </div>

              <ProtectedComponent permission={Permission.MANAGE_STAFF}>
                <button
                  onClick={handleCreateUser}
                  className="btn-primary flex items-center gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  {t('settings.addStaff')}
                </button>
              </ProtectedComponent>
            </div>

            {/* 员工列表 */}
            <StaffPermissionList
              onViewUser={handleViewUser}
              onEditUser={handleEditUser}
            />
          </>
        )}

        {viewMode === 'create' && (
          <>
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={handleBackToList}
                className="btn-secondary flex items-center gap-2"
              >
                ← {t('common.back')}
              </button>
              <div className="flex items-center gap-3">
                <UserPlus className="h-6 w-6 text-primary-600" />
                <h1 className="text-2xl font-bold text-gray-900">
                  {t('settings.addStaff')}
                </h1>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 max-w-lg">
              <form onSubmit={handleCreateSubmit} className="space-y-4">
                {createError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {createError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('auth.username')}
                  </label>
                  <input
                    type="text"
                    required
                    value={createForm.username}
                    onChange={(e) => setCreateForm((f) => ({ ...f, username: e.target.value }))}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.fullName')}
                  </label>
                  <input
                    type="text"
                    required
                    value={createForm.full_name}
                    onChange={(e) => setCreateForm((f) => ({ ...f, full_name: e.target.value }))}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.emailAddress')}
                  </label>
                  <input
                    type="email"
                    required
                    value={createForm.email}
                    onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.role')}
                  </label>
                  <select
                    value={createForm.role}
                    onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value as Role }))}
                    className="input w-full"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {t(`debug.roles.${r}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.initialPassword')}
                  </label>
                  <input
                    type="password"
                    required
                    value={createForm.password}
                    onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                    className="input w-full"
                    placeholder={t('settings.passwordHint')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.aiRole')}
                  </label>
                  <select
                    value={createForm.ai_role}
                    onChange={(e) =>
                      setCreateForm((f) => ({
                        ...f,
                        ai_role: e.target.value as CreateForm['ai_role'],
                      }))
                    }
                    className="input w-full"
                  >
                    <option value="super_admin">{t('permissionManager.adminRoleDesc')}</option>
                    <option value="manager">{t('permissionManager.managerRoleDesc')}</option>
                    <option value="supervisor">{t('permissionManager.supervisorRoleDesc')}</option>
                    <option value="staff">{t('permissionManager.staffRoleDesc')}</option>
                  </select>
                </div>

                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={createForm.send_welcome_email}
                    onChange={(e) =>
                      setCreateForm((f) => ({ ...f, send_welcome_email: e.target.checked }))
                    }
                    />
                  {t('settings.sendWelcomeEmail')}
                </label>

                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={createForm.ai_enabled}
                    onChange={(e) =>
                      setCreateForm((f) => ({ ...f, ai_enabled: e.target.checked }))
                    }
                    />
                  {t('settings.allowNexcaveAI')}
                </label>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="btn-primary"
                  >
                    {createLoading ? t('common.saving') : t('common.save')}
                  </button>
                  <button
                    type="button"
                    onClick={handleBackToList}
                    className="btn-secondary"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}

        {viewMode === 'edit' && (
          <>
            {/* 权限编辑界面 */}
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={handleBackToList}
                className="btn-secondary flex items-center gap-2"
              >
                ← {t('common.back')}
              </button>

              <div className="flex items-center gap-3">
                <Crown className="h-6 w-6 text-amber-600" />
                <h1 className="text-2xl font-bold text-gray-900">
                  {t('settings.permissions')}
                </h1>
              </div>
            </div>

            {selectedUser && (
              <PermissionManager
                user={selectedUser}
                onSave={handleSavePermissions}
                readonly={false}
              />
            )}
          </>
        )}
      </div>
    </ProtectedComponent>
  );
};

export default StaffManagement;
